/**
 * Position Service
 *
 * Handles dropdown position calculations for the ISW.
 *
 * **Migration from Legacy**:
 * - Legacy: calcPositionSuggestionResult() function in index.js
 * - New: Dedicated service with proper encapsulation and dialog detection
 *
 * @module position.service
 */

import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import type { PositionConfig, SearchSettings } from './instant-search.types';

/**
 * Selectors for ISW container elements.
 */
const SELECTORS = {
  RESULTS: '.boost-sd__instant-search-autocomplete-results',
  DESKTOP_CONTAINER:
    '.boost-sd__instant-search-container-desktop .boost-sd__search-widget-init-wrapper',
  MOBILE_CONTAINER:
    '.boost-sd__instant-search-container-mobile .boost-sd__search-widget-init-wrapper',
  ISW_ELEMENT: '#bc-instant-search',
  HEADER: 'header',
  SEARCH_FORM: '.boost-sd__search-form',
} as const;

/**
 * Service for calculating ISW dropdown position.
 *
 * Technical Support teams can extend this service for custom positioning:
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('PositionService', (PositionService) => {
 *   return class CustomPositionService extends PositionService {
 *     // Add custom offset for a specific theme
 *     protected getGap() {
 *       return window.myThemeConfig?.iswGap || super.getGap();
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class PositionService {
  /** Gap between input and dropdown */
  protected readonly GAP = 4;
  /** Border width compensation */
  protected readonly BORDER = 2;
  /** Default z-index for dropdown */
  protected readonly DEFAULT_Z_INDEX = 999999998;
  /** Reduced z-index when below header */
  protected readonly HEADER_OVERLAP_Z_INDEX = 2;
  /** Scroll debounce delay in ms */
  protected readonly SCROLL_DEBOUNCE_MS = 10;
  /** Bottom margin for viewport calculation */
  protected readonly BOTTOM_MARGIN = 20;

  /** Active scroll handler reference for cleanup */
  private scrollHandler: (() => void) | null = null;
  /** Debounce timer for scroll handler */
  private scrollDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Debug mode flag */
  private debugMode = false;

  constructor(protected appService: AppService) {}

  /**
   * Enable or disable debug logging.
   *
   * @param enabled - Whether to enable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Log debug messages when debug mode is enabled.
   *
   * @param message - Message to log
   * @param data - Optional data to include
   */
  protected log(message: string, data?: unknown): void {
    if (this.debugMode) {
      this.appService.logger.debug(`[PositionService] ${message}`, data ?? '');
    }
  }

  /**
   * Check if current device is mobile or tablet (≤ 991 px).
   *
   * Matches the legacy `isMobile(isTabletPortraitMax)` threshold so that
   * tablets are positioned with the mobile ISW container, consistent with
   * the full-width style determination in InstantSearchSelectors.
   *
   * @returns True if device width is below the tablet-portrait-max breakpoint
   */
  protected isMobileDevice(): boolean {
    return this.appService.detectDeviceByWidth().includes('tablet_portrait_max');
  }

  /**
   * Check if the current style is full width.
   *
   * **Legacy**: isStyleFullWidth(context)
   * **New**: Single authoritative method — delegates from InstantSearchSelectors
   *
   * @param searchSettings - Search settings from context
   * @returns True if style is full width
   */
  isStyleFullWidth(searchSettings: SearchSettings): boolean {
    const isMobile = this.isMobileDevice();
    const { suggestionStyle, suggestionMobileStyle } = searchSettings;

    return (
      (isMobile && suggestionMobileStyle !== 'style2') ||
      (!isMobile && suggestionStyle === 'style3')
    );
  }

  /**
   * Calculate dropdown position relative to the input element.
   *
   * **Legacy**: calcPositionSuggestionResult(context, input) - inline calculation
   * **New**: Separated service with configurable offsets
   *
   * @param input - The search input element
   * @param searchSettings - Search settings from context
   * @returns Position configuration
   */
  calculatePosition(input: HTMLInputElement, searchSettings: SearchSettings): PositionConfig {
    const rect = input.getBoundingClientRect();
    const iswInDialog = this.isISWInDialog();

    this.log('calculatePosition', {
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      iswInDialog,
      isMobile: this.isMobileDevice(),
    });

    if (iswInDialog) {
      return this.calculateDialogPosition(rect, searchSettings);
    }

    return this.calculateBodyPosition(rect, searchSettings);
  }

  /**
   * Calculate position when ISW is inside a dialog.
   *
   * @param rect - Input element bounding rect
   * @param searchSettings - Search settings
   * @returns Position configuration for dialog mode
   */
  protected calculateDialogPosition(rect: DOMRect, searchSettings: SearchSettings): PositionConfig {
    const availableHeight = window.innerHeight - 200;

    return {
      position: 'absolute',
      top: rect.height + this.getGap() + this.getBorder(),
      left: 0,
      zIndex: undefined, // Use natural stacking in dialog
      maxHeight: this.calculateMaxHeight(searchSettings, availableHeight),
    };
  }

  /**
   * Calculate position when ISW is appended to body.
   *
   * @param rect - Input element bounding rect
   * @param searchSettings - Search settings
   * @returns Position configuration for body mode
   */
  protected calculateBodyPosition(rect: DOMRect, searchSettings: SearchSettings): PositionConfig {
    const isFullWidth = this.isStyleFullWidth(searchSettings);
    const bottomMargin = isFullWidth ? 0 : this.BOTTOM_MARGIN;
    const topBeforeScroll = rect.height + rect.top + this.getBorder() + this.getGap();
    const availableHeight = window.innerHeight - topBeforeScroll - bottomMargin;
    const top = topBeforeScroll + window.scrollY;
    const isRightAligned = this.isRightAligned(rect);

    return {
      position: 'absolute',
      top,
      ...(isRightAligned ? { right: window.innerWidth - rect.right } : { left: rect.left }),
      zIndex: this.DEFAULT_Z_INDEX,
      maxHeight: this.calculateMaxHeight(searchSettings, availableHeight),
    };
  }

  /**
   * Calculate maximum height for the dropdown.
   *
   * **Legacy**: calcMaxHeightByStyle - Hard-coded values based on style
   * **New**: Configurable with style-specific constraints and suggestionMaxHeight support
   *
   * @param searchSettings - Search settings
   * @param availableHeight - Available viewport height
   * @returns Maximum height in pixels
   */
  calculateMaxHeight(searchSettings: SearchSettings, availableHeight: number): number {
    const isMobile = this.isMobileDevice();
    const {
      suggestionStyle,
      suggestionStyle2ProductItemType,
      suggestionMaxHeight = 0,
    } = searchSettings;

    // If suggestionMaxHeight is set and available height exceeds it, use it
    if (suggestionMaxHeight && suggestionMaxHeight > 0 && availableHeight >= suggestionMaxHeight) {
      this.log('calculateMaxHeight: using suggestionMaxHeight', suggestionMaxHeight);
      return suggestionMaxHeight;
    }

    let maxHeight: number;

    const isFullWidth = this.isStyleFullWidth(searchSettings);

    if (isFullWidth) {
      // Full viewport for full-width styles (all mobile/tablet by default, or desktop style3)
      maxHeight = window.innerHeight;
    } else if (
      !isMobile &&
      suggestionStyle === 'style2' &&
      suggestionStyle2ProductItemType === 'list'
    ) {
      // Desktop style2 with list layout
      maxHeight = 564;
    } else {
      // Desktop style1/style2 grid, or mobile style2 (dropdown)
      maxHeight = 640;
    }

    this.log('calculateMaxHeight', {
      isMobile,
      isFullWidth,
      suggestionStyle,
      maxHeight,
      availableHeight,
    });

    // Don't exceed available height
    return Math.min(maxHeight, availableHeight);
  }

  /**
   * Calculate suggested dropdown width based on input element.
   *
   * @param input - The search input element
   * @param searchSettings - Search settings
   * @returns Width in pixels or undefined for auto
   */
  calculateWidth(input: HTMLInputElement, searchSettings: SearchSettings): number | undefined {
    const isFullWidth = this.isStyleFullWidth(searchSettings);

    if (isFullWidth) {
      return window.innerWidth;
    }

    // For non-fullwidth, use input width as minimum
    const rect = input.getBoundingClientRect();
    return rect.width;
  }

  /**
   * Check if an element is inside a dialog.
   *
   * @param element - Element to check
   * @returns True if element is inside a dialog
   */
  isInDialog(element: HTMLElement): boolean {
    return !!element.closest('dialog, [role="dialog"], .modal, [data-modal]');
  }

  /**
   * Check if ISW element has been moved to dialog.
   *
   * @returns True if ISW is in dialog mode
   */
  isISWInDialog(): boolean {
    const iswElement = document.querySelector(SELECTORS.ISW_ELEMENT);
    return iswElement?.getAttribute('data-boost-moved-to-dialog') === 'true';
  }

  /**
   * Determine if dropdown should be right-aligned.
   *
   * @param rect - Input element bounding rect
   * @returns True if input is on the right side of viewport
   */
  protected isRightAligned(rect: DOMRect): boolean {
    return window.innerWidth / 2 < rect.left;
  }

  /**
   * Get gap between input and dropdown.
   * Override for custom spacing.
   */
  protected getGap(): number {
    return this.GAP;
  }

  /**
   * Get border width compensation.
   * Override for themes with different borders.
   */
  protected getBorder(): number {
    return this.BORDER;
  }

  /**
   * Apply position styles to an element.
   *
   * @param element - Element to position
   * @param config - Position configuration
   */
  applyPosition(element: HTMLElement, config: PositionConfig): void {
    requestAnimationFrame(() => {
      element.style.position = config.position;
      element.style.top = `${config.top}px`;

      if (config.left !== undefined) {
        element.style.left = `${config.left}px`;
        element.style.right = 'auto';
      }

      if (config.right !== undefined) {
        element.style.right = `${config.right}px`;
        element.style.left = 'auto';
      }

      if (config.zIndex !== undefined) {
        element.style.zIndex = String(config.zIndex);
      }

      element.style.maxHeight = `${config.maxHeight}px`;

      this.log('applyPosition', config);
    });
  }

  /**
   * Apply max height to the results container inside the dropdown.
   *
   * @param container - The dropdown container element
   * @param maxHeight - Maximum height in pixels
   */
  applyMaxHeightToResults(container: HTMLElement, maxHeight: number): void {
    requestAnimationFrame(() => {
      const resultsElement = container.querySelector<HTMLElement>(SELECTORS.RESULTS);
      if (resultsElement) {
        resultsElement.style.maxHeight = `${maxHeight}px`;
        this.log('applyMaxHeightToResults', maxHeight);
      }
    });
  }

  /**
   * Set up scroll handler to adjust z-index when scrolling.
   *
   * **Legacy**: Inline scroll handler with debounce in calcPositionSuggestionResult
   * **New**: Dedicated method with proper cleanup
   *
   * @param container - The dropdown container element
   * @returns Cleanup function to remove scroll handler
   */
  setupScrollHandler(container: HTMLElement): () => void {
    // Remove existing handler if any
    this.cleanupScrollHandler();

    const handler = (): void => {
      // Clear existing timer
      if (this.scrollDebounceTimer) {
        clearTimeout(this.scrollDebounceTimer);
      }

      this.scrollDebounceTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          const zIndex = this.calculateScrollZIndex();
          container.style.zIndex = String(zIndex);
          this.log('scrollHandler: updated zIndex', zIndex);
        });
      }, this.SCROLL_DEBOUNCE_MS);
    };

    this.scrollHandler = handler;
    window.addEventListener('scroll', handler);

    this.log('setupScrollHandler: handler attached');

    return () => this.cleanupScrollHandler();
  }

  /**
   * Calculate z-index based on scroll position relative to header.
   *
   * When the search form scrolls behind the header, reduce z-index
   * to prevent overlap issues.
   *
   * @returns Appropriate z-index value
   */
  protected calculateScrollZIndex(): number {
    const headers = document.getElementsByTagName(SELECTORS.HEADER);
    const searchForms = document.getElementsByClassName(SELECTORS.SEARCH_FORM.slice(1)); // Remove leading dot

    if (headers.length > 0 && searchForms.length > 0 && window.scrollY > 0) {
      const header = headers[0];
      const searchForm = searchForms[0];
      const searchFormRect = searchForm.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();

      if (searchFormRect.bottom <= headerRect.top + header.clientHeight) {
        return this.HEADER_OVERLAP_Z_INDEX;
      }
    }

    return this.DEFAULT_Z_INDEX;
  }

  /**
   * Clean up scroll handler.
   */
  cleanupScrollHandler(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
      this.log('cleanupScrollHandler: handler removed');
    }

    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = null;
    }
  }

  /**
   * Get the ISW container element for the current device.
   *
   * @returns The container element or null if not found
   */
  getContainerElement(): HTMLElement | null {
    const isMobile = this.isMobileDevice();
    const selector = isMobile ? SELECTORS.MOBILE_CONTAINER : SELECTORS.DESKTOP_CONTAINER;
    return document.querySelector<HTMLElement>(selector);
  }

  /**
   * Full position update flow: calculate, apply, and setup scroll handler.
   *
   * @param input - The search input element
   * @param container - The dropdown container element
   * @param searchSettings - Search settings
   * @returns Cleanup function for scroll handler
   */
  updatePosition(
    input: HTMLInputElement,
    container: HTMLElement,
    searchSettings: SearchSettings
  ): () => void {
    const config = this.calculatePosition(input, searchSettings);
    this.applyPosition(container, config);
    this.applyMaxHeightToResults(container, config.maxHeight);

    // Only setup scroll handler for body-positioned ISW
    if (!this.isISWInDialog()) {
      return this.setupScrollHandler(container);
    }

    return () => {
      /* no-op for dialog */
    };
  }

  /**
   * Cleanup all resources.
   */
  dispose(): void {
    this.cleanupScrollHandler();
    this.log('dispose: cleanup complete');
  }
}
